import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CONTACT } from "@/constants/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", phone: "", vehicle: "", message: "" };

export const DevisForm = ({ onSuccess }) => {
  const [form, setForm] = useState(emptyForm);
  const [loaner, setLoaner] = useState(false);
  const [rental, setRental] = useState(false);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Consentement requis", {
        description: "Veuillez accepter l'utilisation de vos données pour traiter votre demande.",
      });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/devis`, {
        ...form,
        consent,
        loaner_vehicle: loaner,
        rental_vehicle: rental,
      });
      const waText = [
        "Bonjour MECA AUTO CLEAN 01, je souhaite obtenir un devis.",
        `Nom : ${form.name}`,
        `Téléphone : ${form.phone}`,
        `E-mail : ${form.email}`,
        form.vehicle ? `Véhicule : ${form.vehicle}` : null,
        `Demande : ${form.message}`,
        loaner ? "Véhicule de prêt : oui" : null,
        rental ? "Location de véhicule : oui" : null,
      ]
        .filter(Boolean)
        .join("\n");
      window.open(
        `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(waText)}`,
        "_blank",
      );
      toast.success("Demande envoyée", {
        description:
          "WhatsApp s'ouvre pour transmettre votre demande au garage. Une copie est aussi envoyée au garage par e-mail.",
      });
      setForm(emptyForm);
      setLoaner(false);
      setRental(false);
      setConsent(false);
      onSuccess?.();
    } catch {
      toast.error("Envoi impossible", {
        description: `Réessayez dans un instant ou appelez le garage au ${CONTACT.phoneDisplay}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="devis-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          required
          placeholder="Votre nom *"
          value={form.name}
          onChange={set("name")}
          data-testid="devis-name-input"
          aria-label="Votre nom"
        />
        <Input
          required
          type="tel"
          placeholder="Votre téléphone *"
          value={form.phone}
          onChange={set("phone")}
          data-testid="devis-phone-input"
          aria-label="Votre téléphone"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          required
          type="email"
          placeholder="Votre e-mail *"
          value={form.email}
          onChange={set("email")}
          data-testid="devis-email-input"
          aria-label="Votre e-mail"
        />
        <Input
          placeholder="Votre véhicule (marque, modèle)"
          value={form.vehicle}
          onChange={set("vehicle")}
          data-testid="devis-vehicle-input"
          aria-label="Votre véhicule"
        />
      </div>
      <Textarea
        required
        rows={4}
        placeholder="Décrivez votre besoin (panne, entretien, diagnostic...) *"
        value={form.message}
        onChange={set("message")}
        data-testid="devis-message-input"
        aria-label="Votre message"
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-start gap-3 rounded-md border border-border bg-secondary/40 p-3 text-sm transition-colors hover:border-primary/40">
          <Checkbox
            checked={loaner}
            onCheckedChange={(v) => setLoaner(v === true)}
            data-testid="devis-loaner-checkbox"
            aria-label="Véhicule de prêt"
            className="mt-0.5"
          />
          <span>
            <span className="block font-semibold text-foreground">Véhicule de prêt</span>
            <span className="text-muted-foreground">
              Je souhaite un véhicule de remplacement pendant l'intervention
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 rounded-md border border-border bg-secondary/40 p-3 text-sm transition-colors hover:border-primary/40">
          <Checkbox
            checked={rental}
            onCheckedChange={(v) => setRental(v === true)}
            data-testid="devis-rental-checkbox"
            aria-label="Location de véhicule"
            className="mt-0.5"
          />
          <span>
            <span className="block font-semibold text-foreground">Location de véhicule</span>
            <span className="text-muted-foreground">
              Je suis intéressé par la location d'un véhicule
            </span>
          </span>
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        Documents à fournir et conditions :{" "}
        <Link to="/vehicule-de-pret" data-testid="devis-loaner-info-link" className="text-primary underline-offset-4 hover:underline">
          véhicule de prêt
        </Link>
        {" · "}
        <Link to="/location-de-vehicule" data-testid="devis-rental-info-link" className="text-primary underline-offset-4 hover:underline">
          location de véhicule
        </Link>
      </p>
      <label className="flex items-start gap-3 text-sm text-muted-foreground">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          data-testid="devis-consent-checkbox"
          aria-label="Consentement au traitement des données"
          className="mt-0.5"
        />
        <span>
          J'accepte que mes données soient utilisées pour traiter ma demande,
          conformément à la{" "}
          <Link
            to="/politique-de-confidentialite"
            data-testid="devis-privacy-link"
            className="text-primary underline-offset-4 hover:underline"
          >
            politique de confidentialité
          </Link>
          . *
        </span>
      </label>
      <Button
        type="submit"
        disabled={sending}
        data-testid="devis-submit-button"
        className="w-full sm:w-auto"
      >
        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Envoyer ma demande
      </Button>
    </form>
  );
};
